import {Injectable} from '@angular/core';
import {Logger} from '../utils/logger';

/** How often an open embed renews its pass; the server's lasts six hours. */
const RENEW_EVERY_MS = 2 * 60 * 60 * 1000;

/**
 * Private projects this embed opened because their owner authorized the
 * embed's developer (Authorized developers). The server gave this session a
 * pass that reads the project as a View only visitor reads it, for a few
 * hours (CanvasAuthGuardService asks for it).
 *
 * The canvas treats such a project as shared View only: comments as the
 * project's own setting says, as on View only. The pass is renewed while the
 * embed stays open, so a map left open on a wall screen does not lose its
 * project after the first hours.
 */
@Injectable({
  providedIn: 'root'
})
export class EmbedPassState {
  private readonly held = new Map<string, ReturnType<typeof setInterval>>();

  /** Whether this session opened the project with its owner's authorization. */
  has(projectId: string | null | undefined): boolean {
    return !!projectId && this.held.has(projectId);
  }

  /**
   * Keep the project's pass alive: `renew` asks the server again and says
   * whether a pass came back. Renewing stops once one does not (the owner
   * took the authorization back); the project then reads as before.
   */
  hold(projectId: string, renew: () => Promise<boolean>) {
    if (!projectId || this.held.has(projectId)) {
      return;
    }
    const timer = setInterval(() => {
      renew().then(ok => {
        if (!ok) {
          this.release(projectId);
        }
      }).catch(err => Logger.error('Renewing the embed pass failed', err));
    }, RENEW_EVERY_MS);
    this.held.set(projectId, timer);
  }

  release(projectId: string) {
    const timer = this.held.get(projectId);
    if (timer) {
      clearInterval(timer);
    }
    this.held.delete(projectId);
  }
}
